import { useEffect, useState } from "react";
import { BusinessContent } from "../components/business_components/business-content";
import MainLayout from "../components/MainLayout";
import HomeRightSidebar from "../components/home_components/HomeRightSidebar";
import { useParams } from "react-router-dom";
import { getBusinessDetailByPlaceId } from "../services/business";
import { successToast, errorToast } from "../utils/toasters";
import { Loader2 } from "lucide-react";
import {
  addBusinessDiscount,
  createBusinessCatalog,
  addBusinessInfo,
  editBusinessDetails,
  getBusinessCatalog,
} from "../services/business";

export default function BusinessPage() {
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const { google_place_id } = useParams();
  const [businessDetail, setBusinessDetail] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [ownerId, setOwnerId] = useState();
  const [businessId, setBusinessId] = useState(null);
  const [businessOffers, setBusinessOffers] = useState([]);
  const [businessCatalog, setBusinessCatalog] = useState([]);
  const [additionalInfo, setAdditionalInfo] = useState();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    async function fetchBusiness() {
      if (!google_place_id) {
        return;
      }
      setIsProcessing(true);
      try {
        const businessDetail = await getBusinessDetailByPlaceId(
          google_place_id
        );
        setBusinessDetail(businessDetail.placeDetails);
        setIsOwner(!!businessDetail.isOwner);
        setOwnerId(businessDetail.ownerId);
        setBusinessId(businessDetail.businessId);

        // New business data from backend
        setBusinessOffers(businessDetail.businessOffers || []);
        setBusinessCatalog(businessDetail.businessCatalog || []);
        setAdditionalInfo(businessDetail.additionalInfo || {});
      } catch (error) {
        errorToast("Error while fetching business detail");
      } finally {
        setIsProcessing(false);
      }
    }
    fetchBusiness();
  }, [google_place_id]);

  //for additional info
  const handleFormSubmit = async (data) => {
    if (!businessId) {
      errorToast("Business ID is required");
      return;
    }
    if (!data.additionalInfo) {
      errorToast("Additional info cannot be empty");
      return;
    }
    const previousInfo = { ...additionalInfo };

    setAdditionalInfo({ additional_info: data.additionalInfo });
    try {
      setIsLoading(true);

      const payload = {
        business_id: businessId,
        additional_info: data.additionalInfo,
      };
      const response = await addBusinessInfo(payload);
      successToast("Additional info saved successfully!");
      return { success: true };
    } catch (err) {
      setAdditionalInfo(previousInfo);
      errorToast(err.message || "Failed to save additional info");
      return { success: false };
    } finally {
      setIsLoading(false);
    }
  };

  //for business offer
  const handleBusinessOfferSubmit = async (data) => {
    if (!businessId) {
      return errorToast("Business ID is required to add discount");
    }

    setIsLoading(true);
    const previousOffers = [...businessOffers];
    const mappedOffer = {
      title: data.type || "Offer",
      description: data.description,
      discount_percentage: Number(data.percentage).toFixed(2),
    };
    setBusinessOffers([mappedOffer]);
    try {
      const payload = {
        business_id: businessId,
        title: data.type || "Offer",
        discount_percentage: Number(data.percentage),
        description: data.description,
      };
      const response = await addBusinessDiscount(payload);
      if (response.success) {
        successToast("Offer added successfully!");
        return { success: true };
      } else {
        setBusinessOffers(previousOffers);
        errorToast("Failed to add offer");
        return { success: false };
      }
    } catch (err) {
      setBusinessOffers(previousOffers);
      errorToast(err.message);
      return { success: false };
    } finally {
      setIsLoading(false);
    }
  };

  //for business offer
  const handleDetailsSubmit = async (data) => {
    if (!businessId) {
      errorToast("Business ID is required");
      return;
    }

    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("contactemail", data.contactemail);
      formData.append("contactphone", data.contactphone);
      formData.append("contactotherphone", data.contactotherphone);
      formData.append("businessId", businessId);

      if (data.certificateFile) {
        formData.append("business_certificate", data.certificateFile);
      }
      if (data.imageFile) {
        formData.append("business_image", data.imageFile);
      }
      if (data.videoFile) {
        formData.append("business_video",data.videoFile);
      }
      const response = await editBusinessDetails(formData);
      successToast("Business details updated successfully!");
      return { success: true };
    } catch (err) {
      errorToast(err.message || "Failed to update business details");
      return { success: false };
    } finally {
      setIsLoading(false);
    }
  };

  //for business offer
  const handleBusinessCatalogSubmit = async (data) => {
    setIsLoading(true);

    if (!businessId) {
      errorToast("Business ID is required to create catalog");
      setIsLoading(false);
      return;
    }

    // transform items
    const transformedItems = data.items.map((item) => ({
      item_name: item.name,
      price: isNaN(Number(item.price)) ? 0 : Number(item.price),
      description: item.description || "",
      quantity: item.quantity || 1,
    }));

    const previousCatalog = [...businessCatalog];

    // create an optimistic catalog object
    const optimisticCatalog = {
      catalog_image_url: data.catalogImage
        ? URL.createObjectURL(data.catalogImage)
        : null,
      items: transformedItems.map((i) => ({
        item_name: i.item_name,
        price: i.price,
        description: i.description,
      })),
    };

    // update UI optimistically
    setBusinessCatalog([optimisticCatalog]);

    try {
      const formData = new FormData();
      formData.append("businessId", businessId);
      formData.append("category", data.category);
      formData.append("items", JSON.stringify(transformedItems));
      formData.append("catalog_image", data.catalogImage);
      const response = await createBusinessCatalog(formData);
      successToast("Catalog updated successfully!");
      return { success: true };
    } catch (err) {
      errorToast(err.message);
      setBusinessCatalog(previousCatalog);
      return { success: false };
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!isSidebarOpen);
  };

  return (
    <MainLayout rightSidebar={<HomeRightSidebar />} padding={false} pad={false}>
      <div className="">
        {isProcessing && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50">
            <Loader2 className="animate-spin" width={64} height={64} />
          </div>
        )}

        {businessDetail && (
          <BusinessContent
            businessDetail={businessDetail}
            isOwner={isOwner}
            businessId={businessId}
            businessOffers={businessOffers}
            additionalInfo={additionalInfo}
            businessCatalog={businessCatalog}
            handleFormSubmit={handleFormSubmit}
            handleBusinessOfferSubmit={handleBusinessOfferSubmit}
            handleBusinessCatalogSubmit={handleBusinessCatalogSubmit}
            handleDetailsSubmit={handleDetailsSubmit}
            isLoading={isLoading}
          />
        )}
      </div>
    </MainLayout>
  );
}
